import { Injectable, inject } from '@angular/core';
import type { MatSnackBarRef } from '@angular/material/snack-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MsalService } from '@azure/msal-angular';
import { AppInsightsService } from './AppInsights/app-insights.service';

interface QueuedMessage {
  message: string;
  type: 'success' | 'error';
  duration: number;
}

@Injectable({
  providedIn: 'root'
})
export class AppNotificationService {
  private readonly snackBar = inject(MatSnackBar);
  private readonly msalService = inject(MsalService);
  private readonly appInsights = inject(AppInsightsService);
  private messageQueue: QueuedMessage[] = [];
  private currentSnackBar: MatSnackBarRef<unknown> | null = null;
  private isProcessingQueue = false;
  private readonly MAX_DISMISSAL_MESSAGES = 3;

  /**
   * Display a success message in a snackbar
   * @param msg - The message to display
   * @param seconds - Duration in seconds (null = wait for user dismiss)
   */
  showSuccessMessage(msg: string, seconds: number | null = null): void {
    this.addToQueue({
      message: msg,
      type: 'success',
      duration: seconds ? seconds * 1000 : 5000
    });
  }

  /**
   * Display an error message extracted from an error object
   * @param error - The error object to inspect
   */
  showErrorObjectMessage(error: unknown): void {
    let extractedMessage: string;

    // Type guard to check if error is an object with status property
    const hasStatus = (err: unknown): err is { status: number } =>
      typeof err === 'object' && err !== null && 'status' in err;

    // 403 is handled globally via ForbiddenService — no toast needed
    if (hasStatus(error) && error.status === 403) {
      return;
    }
    
    const hasErrorDetail = (err: unknown): err is { error: { detail: string } } =>
      typeof err === 'object' && err !== null && 'error' in err && 
      typeof (err as { error: unknown }).error === 'object' && 
      (err as { error: unknown }).error !== null &&
      'detail' in (err as { error: object }).error;

    const hasModelBindingErrors = (err: unknown): err is { error: { errors: Record<string, string[]> } } =>
      typeof err === 'object' && err !== null && 'error' in err && 
      typeof (err as { error: unknown }).error === 'object' && 
      (err as { error: unknown }).error !== null &&
      'errors' in (err as { error: object }).error &&
      typeof (err as { error: { errors: unknown } }).error.errors === 'object' &&
      !Array.isArray((err as { error: { errors: unknown } }).error.errors);

    const hasMessage = (err: unknown): err is { message: string } =>
      typeof err === 'object' && err !== null && 'message' in err;

    const hasErrorString = (err: unknown): err is { error: string } =>
      typeof err === 'object' && err !== null && 'error' in err &&
      typeof (err as { error: unknown }).error === 'string';

    // Determine if this is a 400 error with detail (expected validation error)
    const is400WithDetail = hasStatus(error) && error.status === 400 && hasErrorDetail(error);

    if (hasErrorDetail(error)) {
      extractedMessage = 'Error: ';
    } else if (hasModelBindingErrors(error)) {
      extractedMessage = 'Errors: ';  
    } else {
      extractedMessage = 'Unexpected error';
    }
    
    if (hasErrorDetail(error) && !extractedMessage.includes(error.error.detail)) {
      extractedMessage += ` - ${error.error.detail}`;
    } else if (hasModelBindingErrors(error)) {
      const errorParts: string[] = [];
      for (const fieldName in error.error.errors) {
        const messages = error.error.errors[fieldName];
        errorParts.push(`${fieldName}: ${messages.join(', ')}`);
      }
      extractedMessage += errorParts.join('; ');
    } else {
      if (hasMessage(error) && !extractedMessage.includes(error.message)) {
        extractedMessage += ` - ${error.message}`;
      }

      if (hasErrorString(error) && !extractedMessage.includes(error.error)) {
        extractedMessage += ` - ${error.error}`;
      }
    }

    // Log to Application Insights if this is NOT a 400 error with detail
    if (!is400WithDetail) {
      this.logErrorToAppInsights(error, extractedMessage);
    }

    this.showErrorMessage(extractedMessage);
  }

  /**
   * Log error to Application Insights with user context and stack trace
   */
  private logErrorToAppInsights(error: unknown, extractedMessage: string): void {
    try {
      // Get user information from MSAL
      const accounts = this.msalService.instance.getAllAccounts();
      const userAccount = accounts.length > 0 ? accounts[0] : null;
      
      // Extract stack trace from PropertyMDM angular app only
      const stack = error instanceof Error ? error.stack : undefined;
      const filteredStack = this.filterStackTrace(stack);

      // Build properties object
      const properties: Record<string, string> = {
        extractedMessage,
        username: userAccount?.username || 'unknown',
        //upn: userAccount?.username || 'unknown', // username is the UPN in MSAL
        displayName: userAccount?.name || 'unknown',
        //localAccountId: userAccount?.localAccountId || 'unknown',
        source: 'AppNotificationService.showErrorObjectMessage'
      };

      // Add filtered stack trace if available
      if (filteredStack) {
        properties['stackTrace'] = filteredStack;
      }

      // Add error details if available
      if (typeof error === 'object' && error !== null) {
        if ('status' in error) {
          properties['httpStatus'] = String((error as { status: unknown }).status);
        }
        if ('url' in error) {
          properties['url'] = String((error as { url: unknown }).url);
        }
      }

      // Track as exception if it's an Error object, otherwise as trace
      if (error instanceof Error) {
        this.appInsights.trackException(error, 3); // Severity: Error
        this.appInsights.trackTrace('HTTP Error - ' + extractedMessage, properties);
      } else {
        this.appInsights.trackTrace('HTTP Error (non-Error object) - ' + extractedMessage, properties);
      }
    } catch (loggingError) {
      // Silently fail - don't let logging errors break the app
      console.error('Failed to log error to Application Insights:', loggingError);
    }
  }

  /**
   * Filter stack trace to only include PropertyMDM angular app methods
   */
  private filterStackTrace(stack: string | undefined): string | undefined {
    if (!stack) return undefined;

    const lines = stack.split('\n');
    const filtered = lines.filter(line => {
      // Include lines that reference our app code (not node_modules, vendor, etc.)
      return (
        line.includes('app/src/app') ||
        line.includes('property-mdm-frontend') ||
        (line.trim().startsWith('at ') && 
         !line.includes('node_modules') && 
         !line.includes('webpack') &&
         !line.includes('zone.js') &&
         !line.includes('vendor.js') &&
         !line.includes('main.js') &&
         !line.includes('chunk.js'))
      );
    });

    return filtered.length > 0 ? filtered.join('\n') : undefined;
  }

  /**
   * Display an error message with red error icon
   * @param errmsg - The error message to display
   */
  showErrorMessage(errmsg: string): void {
    this.addToQueue({
      message: `⛔ ${errmsg}`,
      type: 'error',
      duration: 7000
    });
  }

  /**
   * Add a message to the queue, enforcing the limit on dismissal-required messages
   */
  private addToQueue(queuedMsg: QueuedMessage): void {
    // If this is a dismissal-required message (duration: 0)
    if (queuedMsg.duration === 0) {
      // Count how many dismissal-required messages are already in the queue
      const dismissalCount = this.messageQueue.filter(msg => msg.duration === 0).length;
      
      // If we're at or over the limit, remove the oldest dismissal-required message
      if (dismissalCount >= this.MAX_DISMISSAL_MESSAGES) {
        const oldestDismissalIndex = this.messageQueue.findIndex(msg => msg.duration === 0);
        if (oldestDismissalIndex !== -1) {
          this.messageQueue.splice(oldestDismissalIndex, 1);
        }
      }
    }
    
    this.messageQueue.push(queuedMsg);
    this.processQueue();
  }

  /**
   * Process the message queue, showing messages one at a time
   */
  private processQueue(): void {
    if (this.isProcessingQueue || this.messageQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;
    const queuedMsg = this.messageQueue.shift()!;

    const panelClass = queuedMsg.type === 'success' 
      ? ['multiline-snackbar', 'success-snackbar']
      : ['multiline-snackbar', 'error-snackbar'];

    this.currentSnackBar = this.snackBar.open(queuedMsg.message, 'Dismiss', {
      duration: queuedMsg.duration,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClass
    });

    this.currentSnackBar.afterDismissed().subscribe(() => {
      this.currentSnackBar = null;
      this.isProcessingQueue = false;
      // Process next message in queue if any
      if (this.messageQueue.length > 0) {
        this.processQueue();
      }
    });
  }
}
